import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Edit as EditIcon, Check as CheckIcon, Close as CloseIcon, Menu as MenuIcon } from '@mui/icons-material';

import SearchApi from '../../api/platform/searchApi';
import SearchSessionApi from '../../api/platform/searchSessionApi';
import clinicalTrialsGovLogo from '../../assets/images/clinical-trials-gov-logo-white.png';
import companyProfileLogo from '../../assets/images/company-logo-transparent.png';
import fdaLogo from '../../assets/images/fda-logo-transparent.png';
import newsLogo from '../../assets/images/news-logo-transparent.png';
import usptoLogo from '../../assets/images/uspto-logo-white.png';
import ClinicalTrials from '../../components/DataSections/ClinicalTrials/ClinicalTrials';
import CompanyProfile from '../../components/DataSections/Company/Company';
import FDA from '../../components/DataSections/FDA/FDA';
import News from '../../components/DataSections/News/News';
import USPTO from '../../components/DataSections/USPTO/USPTO';
import { useSystemError } from '../../components/Error/SystemErrorContext';
import SearchModal from '../../components/Search/SearchModal';
import SavedSearchesModal from '../../components/Search/SavedSearchesModal';
import SaveModal from '../../components/Search/SaveModal';
import styles from '../../css/data-mode/data-mode.module.css';
import { ClinicalTrialsModel } from '../../models/data-sections/clinical-trials/clinicalTrialsModel';
import { CompanyProfileModel } from '../../models/data-sections/company/profileModel';
import { Fda510kModel } from '../../models/data-sections/fda/510kModel';
import { FdaEventModel } from '../../models/data-sections/fda/eventModel';
import { FdaEnforcementModel } from '../../models/data-sections/fda/enforcementModel';
import { FdaPmaModel } from '../../models/data-sections/fda/pmaModel';
import { FdaRecallModel } from '../../models/data-sections/fda/recallModel';
import { FdaRegistrationListingModel } from '../../models/data-sections/fda/registrationlistingModel';
import { FdaUdiModel } from '../../models/data-sections/fda/udiModel';
import { NewsArticleModel } from '../../models/data-sections/news/articleModel';
import { UsptoApplicationModel } from '../../models/data-sections/uspto/applicationModel';
import { UsptoAssignmentModel } from '../../models/data-sections/uspto/assignmentModel';
import { UsptoGrantModel } from '../../models/data-sections/uspto/grantModel';
import { SearchCriteriaModel } from '../../models/platform/searchCriteriaModel';
import { SearchExecuteUnifiedModel } from '../../models/platform/searchExecuteUnifiedModel';
import { SearchesModel } from '../../models/platform/searchesModel';
import { ResponseErrorModel } from '../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../utils/errorUtils';
import { saveSearch } from '../../utils/searchUtils';

const DataMode: React.FC = () => {
  const navigate = useNavigate();

  const [searchName, setSearchName] = useState<string | null>(null);
  const [tempSearchName, setTempSearchName] = useState<string | null>(null);
  const [selectedSearchId, setSelectedSearchId] = useState<string | null>(null);
  const [searchExecutionId, setSearchExecutionId] = useState<string | null>(null);
  const [searchExecutionTime, setSearchExecutionTime] = useState<string | null>(null);

  const [searchCriteria, setSearchCriteria] = useState<SearchCriteriaModel | null>(null);

  const [tempSearch, setTempSearch] = useState<SearchesModel | null>(null);
  const [pendingExecute, setPendingExecute] = useState<boolean>(false);
  const [isSearchExecuted, setIsSearchExecuted] = useState(false);
  const [wasSearchSaved, setWasSearchSaved] = useState(false);
  const [isNewSearch, setIsNewSearch] = useState(false);

  const [isEditingSearchName, setIsEditingSearchName] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isSavedSearchModalOpen, setIsSavedSearchModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  const [selectedTab, setSelectedTab] = useState<string>('clinical-trials');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [progressText, setProgressText] = useState('');


  const [clinicalTrialsStudiesExecuted, setClinicalTrialsStudiesExecuted] = useState(false);
  const [clinicalTrialsStudiesData, setClinicalTrialsStudiesData] = useState<ClinicalTrialsModel[] | null>(null);

  const [companyProfileExecuted, setCompanyProfileExecuted] = useState(false);
  const [companyProfileData, setCompanyProfileData] = useState<CompanyProfileModel[] | null>(null);

  const [fdaExecuted, setFdaExecuted] = useState(false);
  const [fdaK510Data, setFdaK510Data] = useState<Fda510kModel[] | null>(null);
  const [fdaEnforcementData, setFdaEnforcementData] = useState<FdaEnforcementModel[] | null>(null);
  const [fdaEventsData, setFdaEventsData] = useState<FdaEventModel[] | null>(null);
  const [fdaPmaData, setFdaPmaData] = useState<FdaPmaModel[] | null>(null);
  const [fdaRecallData, setFdaRecallData] = useState<FdaRecallModel[] | null>(null);
  const [fdaRegistrationListingData, setFdaRegistrationListingData] = useState<FdaRegistrationListingModel[] | null>(null);
  const [fdaUdiData, setFdaUdiData] = useState<FdaUdiModel[] | null>(null);

  const [newsExecuted, setNewsExecuted] = useState(false);
  const [newsArticlesData, setNewsArticlesData] = useState<NewsArticleModel[] | null>(null);

  const [usptoExecuted, setUsptoExecuted] = useState(false);
  const [usptoApplicationData, setUsptoApplicationData] = useState<UsptoApplicationModel[] | null>(null);
  const [usptoAssignmentData, setUsptoAssignmentData] = useState<UsptoAssignmentModel[] | null>(null);
  const [usptoGrantData, setUsptoGrantData] = useState<UsptoGrantModel[] | null>(null);

  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const { triggerSystemError } = useSystemError();


  /* use effect */
  useEffect(() => {
    const executeSearch = async () => {
      try {
        if (!pendingExecute || !searchCriteria) return;

        await execute(pendingExecute);
      } catch (error) {
        handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
      } finally {
        setPendingExecute(false);
      }
    };

    executeSearch();
  }, [pendingExecute, searchCriteria]);


  /* fetch api */
  const fetchEditNameSave = async (searchId: string, newName: string) => {
    try {
      const response = await SearchApi.updateSearchName(searchId, newName);
      if (response.success) {
        setSearchName(newName);
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const fetchCreateSearch = async (search_payload: SearchesModel): Promise<ResponseSuccessModel | ResponseErrorModel | null> => {
    try {
      const response = await SearchApi.createSearch(search_payload);
      if (response.success) {
        const data = (response as ResponseSuccessModel);
        return data;
      } else {
        const error = (response as ResponseErrorModel);
        return error;
      }
    } catch (error: unknown) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
      return null;
    }
  };

  const fetchUpdateSearch = async (selected_search_id: string, search_payload: SearchesModel): Promise<ResponseSuccessModel | ResponseErrorModel | null> => {
    try {
      const response = await SearchApi.updateSearch(selected_search_id, search_payload);
      if (response.success) {
        const data = (response as ResponseSuccessModel);
        return data;
      } else {
        const error = (response as ResponseErrorModel);
        return error;
      }
    } catch (error: unknown) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
      return null;
    }
  };


  /* functions */
  const execute = async (wasExecuted: boolean) => {
    setIsSearchExecuted(wasExecuted);
    setIsLoading(true);
    setProgressText('Initializing search...');

    setClinicalTrialsStudiesData(null);
    setClinicalTrialsStudiesExecuted(false);

    setCompanyProfileData(null);
    setCompanyProfileExecuted(false);

    setFdaEventsData(null);
    setFdaK510Data(null);
    setFdaPmaData(null);
    setFdaExecuted(false);

    setNewsArticlesData(null);

    setUsptoApplicationData(null);
    setUsptoAssignmentData(null);
    setUsptoGrantData(null);
    setUsptoExecuted(false);

    setSearchExecutionTime(new Date().toLocaleString());
    // setSelectedTab('clinical-trials');

    try {
      if (!searchCriteria) {
        let error = new Error('Search criteria is required to execute the search.');

        setIsSearchExecuted(false);
        setIsLoading(false);

        handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);

        return;
      }

      const search: SearchesModel = {
        search_name: searchName || 'Default Search Name',
        search_criteria: searchCriteria
      };

      const response_initiate = await SearchSessionApi.initiateSearchSession(search);
      const { search_execution_id } = response_initiate.data;
      setSearchExecutionId(search_execution_id);

      setProgressText('Searching Clinical Trials, FDA and USPTO data...');

      const response_data = await SearchSessionApi.executeUnifiedSearch(search_execution_id, search);
      const data: SearchExecuteUnifiedModel = response_data.data;

      setClinicalTrialsStudiesData(data.clinical_trials_studies ?? []);

      setCompanyProfileData(data.company_profiles ?? []);

      setFdaK510Data(data.fda_510k ?? []);
      setFdaEnforcementData(data.fda_enforcement ?? []);
      setFdaEventsData(data.fda_event ?? []);
      setFdaPmaData(data.fda_pma ?? []);
      setFdaRecallData(data.fda_recall ?? []);
      setFdaRegistrationListingData(data.fda_registrationlisting ?? []);
      setFdaUdiData(data.fda_udi ?? []);

      setNewsArticlesData(data.news_article ?? []);

      setUsptoApplicationData(data.uspto_application ?? []);
      setUsptoAssignmentData(data.uspto_assignment ?? []);
      setUsptoGrantData(data.uspto_grant ?? []);

      setClinicalTrialsStudiesExecuted(true);
      setFdaExecuted(true);
      setNewsExecuted(true);
      setUsptoExecuted(true);
      setCompanyProfileExecuted(true);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
      setIsSearchExecuted(false);
      setProgressText('');
    } finally {
      setIsLoading(false);
    }
  };

  const openSearchModal = () => {
    setIsNewSearch(true);
    setTempSearch(null);
    setIsSearchModalOpen(true);
  };

  const openSavedSearchModal = () => {
    setIsSavedSearchModalOpen(true);
  };


  /* handle functions */
  const handleCloseModal = (search: SearchesModel | null, wasSaved: boolean, wasExecuted: boolean, isNewSearch: boolean) => {
    try {
      setIsSearchModalOpen(false);
      setIsSavedSearchModalOpen(false);

      if (isNewSearch) {
        setIsSearchExecuted(false);
        setSearchCriteria(null);
        setSearchName(null);
        setSelectedSearchId(null);

        setClinicalTrialsStudiesData(null);
        setClinicalTrialsStudiesExecuted(false);

        setCompanyProfileData(null);
        setCompanyProfileExecuted(false);

        setFdaEventsData(null);
        setFdaK510Data(null);
        setFdaPmaData(null);
        setFdaExecuted(false);

        setNewsArticlesData(null);

        setUsptoApplicationData(null);
        setUsptoAssignmentData(null);
        setUsptoGrantData(null);
        setUsptoExecuted(false);

        if (search) {
          const tempSearchCriteria = search.search_criteria;

          setSearchCriteria(tempSearchCriteria);

          setSearchName(search.search_name || 'Ad Hoc Search');

          setSelectedSearchId(search._id ?? null);
          setWasSearchSaved(wasSaved);

          setPendingExecute(wasExecuted);
        }
      }

      setTempSearch(null);
    } catch (error: any) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleEditNameEdit = () => {
    setIsEditingSearchName(true);
    setTempSearchName(searchName);
  };

  const handleEditNameCancel = () => {
    setIsEditingSearchName(false);
    setTempSearchName(null);
  };

  const handleEditNameSave = () => {
    try {
      if (tempSearchName !== null && selectedSearchId !== null) {
        fetchEditNameSave(selectedSearchId, tempSearchName);
      }

      setIsEditingSearchName(false);
    } catch (error: any) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleExecuteSearch = () => {
    try {
      execute(true);
    } catch (error: any) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleSaveSearch = async (searchName: string) => {
    try {
      if (!searchCriteria) {
        const error = new Error('Search criteria is missing.');
        handleError(mapUnknownErrorToSystemError(error, false), null, (occurred: boolean) => setDidErrorOccur(occurred), triggerSystemError);

        return;
      }

      await saveSearch({
        searchName,
        searchCriteria,
        editedSearch: selectedSearchId
          ? { _id: selectedSearchId, search_name: searchName, search_criteria: searchCriteria }
          : null,
        shouldExecuteAfterSave: false,
        setIsSaveModalOpen,
        onRequestClose: (savedSearch: SearchesModel, wasSaved: boolean) => {
          setSearchName(savedSearch.search_name || '');
          setSelectedSearchId(savedSearch._id || null);
          setWasSearchSaved(wasSaved);
        },
        triggerSystemError,
      });
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, (occurred: boolean) => setDidErrorOccur(occurred), triggerSystemError);
    }
  };

  const handleViewEditSearch = (selectedSearch: SearchesModel) => {
    setTempSearch(selectedSearch);
    setIsSavedSearchModalOpen(false);
    setIsSearchModalOpen(true);
  };

  const handleViewModifyCurrentSearch = () => {
    try {
      if (!searchCriteria || !selectedSearchId || !searchName) {
        throw new Error('Missing required fields: searchCriteria, selectedSearchId, or searchName.');
      }

      const searchToEdit: SearchesModel = {
        _id: selectedSearchId,
        search_name: searchName,
        search_criteria: searchCriteria
      };

      setTempSearch(searchToEdit);
      setIsSearchModalOpen(true);
    } catch (error) {
      handleError(
        mapUnknownErrorToSystemError(error, false),
        null,
        setDidErrorOccur,
        triggerSystemError
      );
    } finally {
      setIsSaveModalOpen(false);
    }
  };

  const handleTabClick = (tab: string) => {
    setSelectedTab(tab);
  };


  /* rendering code */
  const renderClinicalTrials = () => {
    try {
      if (!isSearchExecuted && !searchCriteria) {
        return <p>Please execute a saved search or create a new one.</p>;
      } else if (!isSearchExecuted && searchCriteria) {
        return <p>Please click the green execute button at the top to Search.</p>;
      }

      return (
        <ClinicalTrials
          searchExecutionId={searchExecutionId || ''}
          studies={clinicalTrialsStudiesData}
          isLoading={isLoading}
        />
      );
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const renderCompanyProfile = () => {
    try {
      if (!isSearchExecuted && !searchCriteria) {
        return <p>Please execute a saved search or create a new one.</p>;
      } else if (!isSearchExecuted && searchCriteria) {
        return <p>Please click the green execute button at the top to Search.</p>;
      }

      return <CompanyProfile
        searchExecutionId={searchExecutionId || ''}
        companies={companyProfileData}
        isLoading={isLoading} />;
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const renderFDA = () => {
    try {
      if (!isSearchExecuted && !searchCriteria) {
        return <p>Please execute a saved search or create a new one.</p>;
      } else if (!isSearchExecuted && searchCriteria) {
        return <p>Please click the green execute button at the top to Search.</p>;
      }

      return <FDA
        searchExecutionId={searchExecutionId || ''}
        k510={fdaK510Data}
        enforcements={fdaEnforcementData}
        events={fdaEventsData}
        pma={fdaPmaData}
        recalls={fdaRecallData}
        registration_listings={fdaRegistrationListingData}
        udi={fdaUdiData}
        isLoading={isLoading} />;
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const renderNews = () => {
    try {
      if (!isSearchExecuted && !searchCriteria) {
        return <p>Please execute a saved search or create a new one.</p>;
      } else if (!isSearchExecuted && searchCriteria) {
        return <p>Please click the green execute button at the top to Search.</p>;
      }

      return (
        <News
          searchExecutionId={searchExecutionId || ''}
          articles={newsArticlesData}
          isLoading={isLoading}
        />
      );
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const renderUSPTO = () => {
    try {
      if (!isSearchExecuted && !searchCriteria) {
        return <p>Please execute a saved search or create a new one.</p>;
      } else if (!isSearchExecuted && searchCriteria) {
        return <p>Please click the green execute button at the top to Search.</p>;
      }

      return (
        <USPTO
          searchExecutionId={searchExecutionId || ''}
          applications={usptoApplicationData}
          assignments={usptoAssignmentData}
          grants={usptoGrantData}
          isLoading={isLoading}
        />
      );
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };


  /* return */
  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        <nav className={`${styles.sidebar} ${!isSidebarOpen ? styles.menuClosed : ''}`}>
          <div className={styles.sidebarHeader}>
            <button className={styles.hamburgerButton} onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
              <MenuIcon />
            </button>
          </div>

          <button className={`${styles.menuItem} ${styles.searchesButton}`} onClick={openSavedSearchModal}>
            🔍 Searches
          </button>
          <button className={styles.menuItem} onClick={openSearchModal}>
            ➕ New Search
          </button>
          <hr style={{ border: 'none', borderTop: '1px solid #555', margin: '12px 0' }} />
          {[
            ['company-info', 'Company Info', companyProfileLogo],
            ['news', 'News', newsLogo],
            ['fda', 'FDA', fdaLogo],
            ['uspto', 'USPTO', usptoLogo],
            ['clinical-trials', 'Clinical Trials', clinicalTrialsGovLogo],
          ].map(([key, label, logo]) => (
            <button
              key={key}
              className={`${styles.menuItem} ${selectedTab === key ? styles.selected : ''}`}
              onClick={() => handleTabClick(key)}
            >
              <div className={styles.logoContainer}>
                <img src={logo} alt={label} className={styles.logoImg} />
              </div>
              {label}
            </button>
          ))}
        </nav>

        <main className={styles.content}>
          {selectedSearchId && (
            <div className={styles.searchSection}>
              <div className={styles.searchWrapper}>
                <div className={styles.centeredSearchInfo}>
                  {isEditingSearchName ? (
                    <div className={styles.editingContainer}>
                      <input
                        type="text"
                        value={tempSearchName ?? ''}
                        onChange={(e) => setTempSearchName(e.target.value)}
                        className={styles.searchNameInput}
                      />
                      <CheckIcon className={`${styles.icon} ${styles.checkIcon}`} onClick={handleEditNameSave} />
                      <CloseIcon className={`${styles.icon} ${styles.closeIcon}`} onClick={handleEditNameCancel} />
                    </div>
                  ) : (
                    <>
                      <div className={styles.headerTitleSection}>
                        <div className={styles.editIconWrapper} onClick={handleEditNameEdit}>
                          <EditIcon className={styles.icon} />
                        </div>
                        <span className={styles.searchName}>{searchName}</span>
                      </div>
                      <div className={styles.actionButtonGroup}>
                        <button className={styles.viewModifyButton} onClick={handleViewModifyCurrentSearch}>
                          View / Modify
                        </button>

                        {!isSearchExecuted ? (
                          <button className={styles.executeButton} onClick={handleExecuteSearch}>
                            Execute
                          </button>
                        ) : (
                          <span className={styles.executionTime}>
                            Executed:{' '}
                            {searchExecutionTime
                              ? new Date(searchExecutionTime).toLocaleString('en-US', {
                                dateStyle: 'short',
                                timeStyle: 'short',
                              })
                              : 'N/A'}
                          </span>
                        )}

                        {!wasSearchSaved && (
                          <button className={styles.saveButton} onClick={() => setIsSaveModalOpen(true)}>
                            Save
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <p className={styles.progressText}>{progressText}</p>
            </div>
          ) : (
            <>
              <Outlet />
              {selectedTab === 'company-info' && renderCompanyProfile()}
              {selectedTab === 'news' && renderNews()}
              {selectedTab === 'fda' && renderFDA()}
              {selectedTab === 'uspto' && renderUSPTO()}
              {selectedTab === 'clinical-trials' && renderClinicalTrials()}
            </>
          )}
        </main>

      </div>

      <SearchModal
        isOpen={isSearchModalOpen}
        onRequestClose={handleCloseModal}
        editedSearch={tempSearch}
      />

      <SavedSearchesModal
        isOpen={isSavedSearchModalOpen}
        onRequestClose={handleCloseModal}
        onViewEdit={handleViewEditSearch}
      />

      <SaveModal
        isOpen={isSaveModalOpen}
        onRequestClose={() => setIsSaveModalOpen(false)}
        onSave={(name) => {
          setSearchName(name);
          setWasSearchSaved(true);
          setIsSaveModalOpen(false);
        }}
        initialSearchName={selectedSearchId ? searchName ?? '' : ''}
        isEditMode={!!selectedSearchId}
      />
    </div>
  );

};

export default DataMode;